// src/portals/admin/components/CreatorDetailModal.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, User, DollarSign, Calendar, Heart, Share2, ShoppingBag, BarChart2, Star, Shield, PlayCircle } from "lucide-react";
import Card from "../../../components/ui/Card";
import Button from "../../../components/ui/Button";
import Modal from "../../../components/ui/Modal"; 
import { useData } from "../../../context/DataContext";

const CreatorDetailModal = ({ creator, onClose }) => {
  const navigate = useNavigate();
  const { updateCreatorProfile, adCampaigns, logAction } = useData();
  const [activeTab, setActiveTab] = useState("overview");

  // Local state for modals
  const [isPermissionsOpen, setIsPermissionsOpen] = useState(false);
  const [isCampaignOpen, setIsCampaignOpen] = useState(false);

  if (!creator) return null;

  // Actions
  const handleSuspend = () => {
    const newStatus = creator.status === "Suspended" ? "Active" : "Suspended";
    const action = newStatus === "Suspended" ? "SUSPEND_USER" : "ACTIVATE_USER";
    
    updateCreatorProfile(creator.id, { status: newStatus });
    
    logAction({
        action,
        targetId: creator.id,
        targetName: creator.name,
        actorName: "Super Admin", // In real app, get from AuthContext
        details: `Creator ${newStatus.toLowerCase()} via CRM`
    });
  };

  const handleImpersonate = () => {
    logAction({
        action: "IMPERSONATE",
        targetId: creator.id,
        targetName: creator.name,
        actorName: "Super Admin",
        details: "Admin started impersonation session"
    });
    // In a real app, this would set an impersonation token
    navigate("/creator");
  };

  const savePermissions = () => {
      // Mock saving logic
      logAction({
        action: "UPDATE_PERMISSIONS",
        targetId: creator.id,
        targetName: creator.name,
        actorName: "Super Admin",
        details: "Updated creator permissions"
      });
      setIsPermissionsOpen(false);
  };



  if (!creator) return null;

  // Mock extended data
  const demographics = {
    male: 45,
    female: 52,
    other: 3,
    topCountries: [
      { name: "Ethiopia", percent: 85 },
      { name: "USA", percent: 8 },
      { name: "UAE", percent: 4 },
      { name: "Other", percent: 3 }
    ]
  };

  const revenueStreams = [
    { source: "Subscriptions", amount: 24500, growth: 12 },
    { source: "Merchandise", amount: 15200, growth: 8 },
    { source: "Tips", amount: 5600, growth: -2 },
    { source: "Events", amount: 45000, growth: 25 },
  ];

  const products = [
    { name: "Limited Edition T-Shirt", sales: 156, revenue: 78000, status: "Active" },
    { name: "Signed Poster", sales: 45, revenue: 11250, status: "Low Stock" },
    { name: "Online Masterclass", sales: 89, revenue: 178000, status: "Active" },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <Card className="max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col p-0">
        {/* Header */}
        <div className="p-6 border-b border-slate-100 flex justify-between items-start bg-slate-50">
          <div className="flex gap-4">
            <div className="w-20 h-20 rounded-xl bg-purple-100 flex items-center justify-center overflow-hidden border border-slate-200">
              {creator.profilePictureUrl ? (
                <img src={creator.profilePictureUrl} alt={creator.name} className="w-full h-full object-cover" />
              ) : (
                <User className="w-8 h-8 text-purple-600" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-bold text-slate-800">{creator.name}</h2>
                {true && <div className="bg-blue-500 text-white rounded-full p-0.5"><Star size={12} fill="currentColor" /></div>}
              </div>
              <div className="text-sm font-medium text-slate-500 mb-2">{creator.category} Creator</div>
              
              <div className="flex gap-2">
                <span className={`px-2 py-0.5 text-xs rounded-full font-medium border ${
                  creator.status === "Active" || true
                    ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                    : "bg-red-100 text-red-700 border-red-200"
                }`}>
                  Active
                </span>
                <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-medium border border-purple-200">
                  LTV: {(creator.revenue || 0).toLocaleString()} ETB
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-sm text-slate-500">Total Followers</div>
              <div className="text-xl font-bold text-slate-800">{(creator.followers || 0).toLocaleString()}</div>
            </div>
             <div className="text-right pr-4 border-r border-slate-200">
              <div className="text-sm text-slate-500">Mth Revenue</div>
              <div className="text-xl font-bold text-emerald-600">{(creator.revenue || 0).toLocaleString()} ETB</div>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b border-slate-100 px-6">
          <div className="flex gap-6">
            {["overview", "audience", "products", "campaigns", "financials"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab
                    ? "border-purple-600 text-purple-700"
                    : "border-transparent text-slate-500 hover:text-slate-700"
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6 bg-white">
          {activeTab === "overview" && (
            <div className="space-y-6">
              {/* Performance Cards */}
              <div className="grid grid-cols-4 gap-4">
                <div className="p-4 bg-purple-50 rounded-xl border border-purple-100">
                  <div className="text-sm text-purple-600 mb-1">Engagement Rate</div>
                  <div className="text-2xl font-bold text-slate-800">12.5%</div>
                  <div className="text-xs text-green-600 mt-1">↑ 2.3% vs last month</div>
                </div>
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <div className="text-sm text-blue-600 mb-1">Total Subscribers</div>
                  <div className="text-2xl font-bold text-slate-800">{((creator.followers || 0) * 0.15).toFixed(0)}</div>
                  <div className="text-xs text-green-600 mt-1">↑ 145 new this week</div>
                </div>
                <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                  <div className="text-sm text-emerald-600 mb-1">Avg Order Value</div>
                  <div className="text-2xl font-bold text-slate-800">850 ETB</div>
                  <div className="text-xs text-green-600 mt-1">↑ 12% vs last month</div>
                </div>
                <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
                  <div className="text-sm text-amber-600 mb-1">Retention Rate</div>
                  <div className="text-2xl font-bold text-slate-800">94%</div>
                  <div className="text-xs text-slate-500 mt-1">Top 5% of creators</div>
                </div>
              </div>

              {/* Revenue Breakdown */}
              <div className="grid grid-cols-2 gap-6">
                 <div className="border border-slate-200 rounded-xl p-5">
                   <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                     <BarChart2 size={18} /> Revenue Mix
                   </h3>
                   <div className="space-y-3">
                     {revenueStreams.map((stream, i) => (
                       <div key={i} className="flex justify-between items-center p-2 hover:bg-slate-50 rounded-lg">
                         <div className="flex items-center gap-2">
                           <div className={`w-3 h-3 rounded-full ${['bg-purple-500', 'bg-blue-500', 'bg-emerald-500', 'bg-orange-500'][i]}`} />
                           <span className="text-sm font-medium text-slate-700">{stream.source}</span>
                         </div>
                         <div className="text-right">
                           <div className="text-sm font-bold text-slate-800">{stream.amount.toLocaleString()} ETB</div>
                           <div className={`text-xs ${stream.growth >= 0 ? "text-green-600" : "text-red-500"}`}>
                             {stream.growth > 0 ? "+" : ""}{stream.growth}%
                           </div>
                         </div>
                       </div>
                     ))}
                   </div>
                 </div>

                 <div className="border border-slate-200 rounded-xl p-5">
                   <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                     <User size={18} /> Audience Snapshot
                   </h3>
                   <div className="space-y-4">
                     <div>
                       <div className="flex justify-between text-sm mb-1">
                         <span className="text-slate-600">Gender Split</span>
                         <span className="font-medium">{demographics.female}% F / {demographics.male}% M</span>
                       </div>
                       <div className="h-2 bg-slate-100 rounded-full overflow-hidden flex">
                         <div style={{width: `${demographics.female}%`}} className="bg-pink-400 h-full" />
                         <div style={{width: `${demographics.male}%`}} className="bg-blue-400 h-full" />
                         <div style={{width: `${demographics.other}%`}} className="bg-slate-400 h-full" />
                       </div>
                     </div>
                     
                     <div>
                       <div className="text-sm text-slate-600 mb-2">Top Locations</div>
                       {demographics.topCountries.map((country, i) => (
                         <div key={i} className="flex justify-between items-center text-sm py-1 border-b border-slate-50 last:border-0">
                           <span className="font-medium text-slate-700">{country.name}</span>
                           <span className="text-slate-500">{country.percent}%</span>
                         </div>
                       ))}
                     </div>
                   </div>
                 </div>
              </div>
            </div>
          )}
          
          {activeTab === "products" && (
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-lg font-bold text-slate-800">Active Products</h3>
                <Button size="sm" variant="outline" onClick={() => navigate('/admin/monetization/inventory')}>Manage Inventory</Button>
              </div>
              <div className="overflow-hidden border border-slate-200 rounded-xl">
                 <table className="w-full text-left text-sm">
                   <thead className="bg-slate-50 border-b border-slate-200">
                     <tr>
                       <th className="px-6 py-3 font-medium text-slate-700">Product Name</th>
                       <th className="px-6 py-3 font-medium text-slate-700 text-center">Sales</th>
                       <th className="px-6 py-3 font-medium text-slate-700 text-right">Revenue</th>
                       <th className="px-6 py-3 font-medium text-slate-700 text-center">Status</th>
                     </tr>
                   </thead>
                   <tbody className="divide-y divide-slate-100">
                     {products.map((product, i) => (
                       <tr key={i} className="hover:bg-slate-50">
                         <td className="px-6 py-4 font-medium text-slate-800">{product.name}</td>
                         <td className="px-6 py-4 text-slate-600 text-center">{product.sales}</td>
                         <td className="px-6 py-4 text-slate-800 font-medium text-right">{product.revenue.toLocaleString()} ETB</td>
                         <td className="px-6 py-4 text-center">
                           <span className={`px-2 py-1 rounded-full text-xs font-medium border ${
                             product.status === "Active" 
                               ? "bg-emerald-100 text-emerald-700 border-emerald-200" 
                               : "bg-amber-100 text-amber-700 border-amber-200"
                           }`}>
                             {product.status}
                           </span>
                         </td>
                       </tr>
                     ))}
                   </tbody>
                 </table>
               </div>
            </div>
          )}

          {activeTab === "audience" && (
            <div className="space-y-6">
               <div className="border border-slate-200 rounded-xl p-5">
                 <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                   <User size={18} /> Audience Demographics
                 </h3>
                 <div className="space-y-4">
                   <div>
                     <div className="flex justify-between text-sm mb-1">
                       <span className="text-slate-600">Gender Split</span>
                       <span className="font-medium">{demographics.female}% F / {demographics.male}% M</span>
                     </div>
                     <div className="h-2 bg-slate-100 rounded-full overflow-hidden flex">
                       <div style={{width: `${demographics.female}%`}} className="bg-pink-400 h-full" />
                       <div style={{width: `${demographics.male}%`}} className="bg-blue-400 h-full" />
                       <div style={{width: `${demographics.other}%`}} className="bg-slate-400 h-full" />
                     </div>
                   </div>
                   
                   <div>
                     <div className="text-sm text-slate-600 mb-2">Top Locations</div>
                     {demographics.topCountries.map((country, i) => (
                       <div key={i} className="flex justify-between items-center text-sm py-1 border-b border-slate-50 last:border-0">
                         <span className="font-medium text-slate-700">{country.name}</span>
                         <span className="text-slate-500">{country.percent}%</span>
                       </div>
                     ))}
                   </div>

                   <div className="grid grid-cols-2 gap-4 pt-2">
                      <div className="bg-slate-50 p-3 rounded-lg">
                          <div className="text-xs text-slate-500 mb-2 font-medium uppercase">Age Distribution</div>
                          <div className="space-y-1">
                              <div className="flex justify-between text-xs"><span className="text-slate-600">18-24</span><span className="font-bold">45%</span></div>
                              <div className="w-full bg-slate-200 h-1.5 rounded-full"><div className="bg-purple-500 h-1.5 rounded-full" style={{width: '45%'}}></div></div>
                              
                              <div className="flex justify-between text-xs mt-2"><span className="text-slate-600">25-34</span><span className="font-bold">30%</span></div>
                              <div className="w-full bg-slate-200 h-1.5 rounded-full"><div className="bg-blue-500 h-1.5 rounded-full" style={{width: '30%'}}></div></div>
                          </div>
                      </div>
                      <div className="bg-slate-50 p-3 rounded-lg">
                          <div className="text-xs text-slate-500 mb-2 font-medium uppercase">Top Interests</div>
                          <div className="flex flex-wrap gap-1">
                              {['Music', 'Fashion', 'Tech', 'Travel', 'Lifestyle'].map(tag => (
                                  <span key={tag} className="text-xs px-2 py-1 bg-white border border-slate-200 rounded text-slate-600">{tag}</span>
                              ))}
                          </div>
                      </div>
                   </div>
                 </div>
               </div>
            </div>
          )}

          {activeTab === "campaigns" && (
             <div className="space-y-4">
               <div className="flex justify-between items-center mb-2">
                 <h3 className="text-lg font-bold text-slate-800">Active Campaigns</h3>
                 <Button size="sm" variant="outline">View All</Button>
               </div>
               <div className="bg-slate-50 rounded-xl p-8 text-center border border-dashed border-slate-300">
                  <div className="text-slate-500">No active campaigns for this creator yet.</div>
                  <Button size="sm" className="mt-4" onClick={() => setIsCampaignOpen(true)}>Assign Campaign</Button>
               </div>
            </div>
          )}

          {activeTab === "financials" && (
             <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                   <Card className="bg-emerald-50 border-emerald-100">
                      <div className="text-sm text-emerald-600 mb-1">Total Lifetime Revenue</div>
                      <div className="text-2xl font-bold text-slate-800">{(creator.revenue || 0).toLocaleString()} ETB</div>
                   </Card>
                   <Card className="bg-blue-50 border-blue-100">
                      <div className="text-sm text-blue-600 mb-1">Pending Payouts</div>
                      <div className="text-2xl font-bold text-slate-800">{(creator.revenue * 0.15 || 0).toLocaleString()} ETB</div>
                   </Card>
                </div>

                <div className="border border-slate-200 rounded-xl p-5">
                   <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                     <BarChart2 size={18} /> Revenue Breakdown
                   </h3>
                   <div className="space-y-3">
                     {revenueStreams.map((stream, i) => (
                       <div key={i} className="flex justify-between items-center p-2 hover:bg-slate-50 rounded-lg">
                         <div className="flex items-center gap-2">
                           <div className={`w-3 h-3 rounded-full ${['bg-purple-500', 'bg-blue-500', 'bg-emerald-500', 'bg-orange-500'][i]}`} />
                           <span className="text-sm font-medium text-slate-700">{stream.source}</span>
                         </div>
                         <div className="text-right">
                           <div className="text-sm font-bold text-slate-800">{stream.amount.toLocaleString()} ETB</div>
                           <div className={`text-xs ${stream.growth >= 0 ? "text-green-600" : "text-red-500"}`}>
                             {stream.growth > 0 ? "+" : ""}{stream.growth}%
                           </div>
                         </div>
                       </div>
                     ))}
                   </div>
                 </div>
             </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-between items-center">
          <div className="text-sm text-slate-500">
            Creator ID: <span className="font-mono">{creator.id || "CX-88392"}</span>
          </div>
          <div className="flex gap-3">
             <Button 
               variant="outline" 
               className={`${creator.status === "Suspended" ? "text-emerald-600 border-emerald-200 hover:bg-emerald-50" : "text-red-600 border-red-200 hover:bg-red-50"}`}
               onClick={handleSuspend}
             >
              {creator.status === "Suspended" ? "Activate Account" : "Suspend Account"}
            </Button>
            <Button variant="outline" onClick={() => setIsPermissionsOpen(true)}>Manage Permissions</Button>
            <Button onClick={handleImpersonate}>Impersonate / View As</Button>
          </div>
        </div>
      </Card>
      
      {/* Permissions Modal */}
      <Modal isOpen={isPermissionsOpen} onClose={() => setIsPermissionsOpen(false)} title={`Permissions: ${creator.name}`}>
         <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg text-purple-600"><PlayCircle size={20}/></div>
                    <div>
                        <div className="font-medium text-slate-800">Live Streaming</div>
                        <div className="text-xs text-slate-500">Can start live sessions</div>
                    </div>
                </div>
                <div className="relative inline-flex h-6 w-11 items-center rounded-full bg-emerald-500">
                    <span className="translate-x-6 inline-block h-4 w-4 transform rounded-full bg-white transition"/>
                </div>
            </div>
             <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg text-blue-600"><ShoppingBag size={20}/></div>
                    <div>
                        <div className="font-medium text-slate-800">Store Access</div>
                        <div className="text-xs text-slate-500">Can manage products</div>
                    </div>
                </div>
                <div className="relative inline-flex h-6 w-11 items-center rounded-full bg-emerald-500">
                    <span className="translate-x-6 inline-block h-4 w-4 transform rounded-full bg-white transition"/>
                </div>
            </div>
             <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-100 rounded-lg text-amber-600"><Star size={20}/></div>
                    <div>
                        <div className="font-medium text-slate-800">Premium Content</div>
                        <div className="text-xs text-slate-500">Can charge for content</div>
                    </div>
                </div>
                <div className="relative inline-flex h-6 w-11 items-center rounded-full bg-slate-200">
                    <span className="translate-x-1 inline-block h-4 w-4 transform rounded-full bg-white transition"/>
                </div>
            </div>
            <div className="flex justify-end mt-4">
                <Button onClick={savePermissions}>Save Permissions</Button>
            </div>
         </div>
      </Modal>

      {/* Campaign Assignment Modal */}
      <Modal isOpen={isCampaignOpen} onClose={() => setIsCampaignOpen(false)} title="Assign Ad Campaign">
          <div className="space-y-4">
              <p className="text-sm text-slate-600">Select a campaign to invite <strong>{creator.name}</strong> to.</p>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                  {adCampaigns.map(campaign => (
                      <div key={campaign.id} className="p-3 border border-slate-200 rounded-lg hover:border-purple-500 cursor-pointer flex justify-between items-center group">
                          <div>
                              <div className="font-medium text-slate-800">{campaign.title}</div>
                              <div className="text-xs text-slate-500">{campaign.brand} • {campaign.budget}</div>
                          </div>
                          <Button size="sm" variant="ghost" className="opacity-0 group-hover:opacity-100 text-purple-600">Select</Button>
                      </div>
                  ))}
              </div>
               <div className="flex justify-end mt-4">
                <Button variant="secondary" onClick={() => setIsCampaignOpen(false)}>Cancel</Button>
              </div>
          </div>
      </Modal>
    </div>
  );
};

export default CreatorDetailModal;
